import { useRef, useState } from 'react'
import axios from 'axios'
import { ImagePlus, Loader2, X } from 'lucide-react'
import { toast } from 'sonner'
import { getApiUrl, resolveImageUrl } from '../lib/utils'

const API = getApiUrl()

/**
 * The picture of a wishlist gift: one optional image per item, uploaded
 * through the same `/upload/image` route the cover photo uses (10 MB cap,
 * PNG / JPEG / WebP / GIF, magic-byte sniffed on the server). The control
 * checks type and size itself before sending so a wrong file is refused
 * without a round-trip, shows the uploaded picture, and lets the host remove
 * it again. The stored value is the URL the server returns.
 */

// Mirrors the backend's MAX_UPLOAD_SIZE / ALLOWED_IMAGE_TYPES; the server
// re-checks both, these only spare the host a failed upload.
const MAX_BYTES = 10 * 1024 * 1024
const ACCEPT = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']

type Props = {
  value?: string | null
  onChange: (url: string) => void
  appLang?: string
  /** Distinguishes the two forms that mount this control. */
  testId?: string
}

const WishlistImageUpload = ({ value, onChange, appLang = 'en', testId = 'wishlist-image' }: Props) => {
  const tr = (en: string, ka: string) => (appLang === 'ka' ? ka : en)
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const upload = async (file?: File | null) => {
    if (!file) return
    if (!ACCEPT.includes(file.type)) {
      toast.error(tr('Please choose a PNG, JPEG, WebP or GIF image', 'აირჩიე PNG, JPEG, WebP ან GIF სურათი'))
      return
    }
    if (file.size > MAX_BYTES) {
      toast.error(tr('The picture must be under 10 MB', 'სურათი 10 MB-ზე ნაკლები უნდა იყოს'))
      return
    }
    setUploading(true)
    const body = new FormData()
    body.append('file', file)
    try {
      const res = await axios.post(`${API}/upload/image`, body, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      onChange(res.data.url)
    } catch (error: any) {
      const detail = error?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : tr('Could not upload the picture', 'სურათი ვერ აიტვირთა'))
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      {value ? (
        <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-[#1b1033]/10 bg-white dark:border-white/10 dark:bg-white/5">
          <img src={resolveImageUrl(value)} alt="" className="h-full w-full object-cover" data-testid={`${testId}-preview`} />
          <button
            type="button"
            onClick={() => onChange('')}
            aria-label={tr('Remove picture', 'სურათის წაშლა')}
            className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-white/90 text-[#1b1033] shadow hover:bg-white dark:bg-[#1e1438]/90 dark:text-[#f4f0ff]"
            data-testid={`${testId}-remove`}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex h-16 w-16 shrink-0 flex-col items-center justify-center gap-0.5 rounded-xl border-2 border-dashed border-[#1b1033]/15 text-[#4a3d66] transition-colors hover:border-[#ff4d8d] hover:text-[#ff4d8d] disabled:opacity-60 dark:border-white/15 dark:text-[#c3b8dd]"
          aria-label={tr('Add a picture of the gift', 'საჩუქრის სურათის დამატება')}
          data-testid={`${testId}-add`}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          <span className="text-[9px] font-medium">{tr('Photo', 'ფოტო')}</span>
        </button>
      )}
      <div className="min-w-0 text-[11px] leading-snug text-[#4a3d66] dark:text-[#c3b8dd]/80">
        {value
          ? tr('Guests see this picture next to the gift.', 'სტუმრები ამ სურათს საჩუქრის გვერდით დაინახავენ.')
          : tr('Optional — a picture of the gift. PNG, JPEG, WebP or GIF, up to 10 MB.', 'არასავალდებულო — საჩუქრის სურათი. PNG, JPEG, WebP ან GIF, 10 MB-მდე.')}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT.join(',')}
        onChange={(e) => upload(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testId}-input`}
      />
    </div>
  )
}

export default WishlistImageUpload
